import { AbstractTailoredWidgetTemplateEditor } from '@/components/template-widgets/AbstractTailoredWidgetEditorTemplate.class';
import { AdvizeoInputDirective } from '@/directives/advizeo-input/advizeo-input.directive';
import type { TemplateFormChangedEvent } from '@/interfaces/TailoredWidget.interface';
import { SharedUiWysiwygComponent } from '@/libs/shared/ui/wysiwyg/lib/shared-ui-wysiwyg.component';
import type { RgbColorHex } from '@/shared/ui/theming';
import { UiFormFieldComponent } from '@/ui/form-field/form-field/ui-form-field';
import { UiIconButtonComponent } from '@/ui/icon-button/ui-icon-button';
import { UiIconComponent } from '@/ui/icon/ui-icon';
import { UiSelectComponent } from '@/ui/select/ui-select';
import { Component, effect, inject, input, output } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

type HtmlContentConfig = { value: string; verticalAlignment: 'up' | 'middle' | 'down'; backgroundColor: RgbColorHex | null };

@UntilDestroy()
@Component({
    styleUrls: ['./template-widget-html-content-editor.component.scss'],
    selector: 'template-widget-html-content-editor',
    templateUrl: 'template-widget-html-content-editor.component.html',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        AdvizeoInputDirective,
        UiSelectComponent,
        UiFormFieldComponent,
        UiIconButtonComponent,
        UiIconComponent,
        SharedUiWysiwygComponent,
        TranslatePipe
    ]
})
export class TemplateWidgetHtmlContentEditorComponent extends AbstractTailoredWidgetTemplateEditor<HtmlContentConfig> {
    private _formBuilder = inject(FormBuilder);
    private _translateService = inject(TranslateService);

    formInputs = input<HtmlContentConfig>();
    config = output<TemplateFormChangedEvent>();

    formGroup = this._formBuilder.group({
        value: this._formBuilder.control<string>(this.formInputs()?.value ?? '', Validators.required),
        verticalAlignment: this._formBuilder.nonNullable.control<'up' | 'middle' | 'down'>(
            this.formInputs()?.verticalAlignment ?? 'middle',
            Validators.required
        ),
        backgroundColor: this._formBuilder.control<RgbColorHex | null>(this.formInputs()?.backgroundColor ?? null)
    });

    options = [
        {
            code: 'up',
            label: this._translateService.instant('TAILORED_WIDGET.HTML_CONTENT.UP')
        },
        {
            code: 'middle',
            label: this._translateService.instant('TAILORED_WIDGET.HTML_CONTENT.MIDDLE')
        },
        {
            code: 'down',
            label: this._translateService.instant('TAILORED_WIDGET.HTML_CONTENT.DOWN')
        }
    ];

    constructor() {
        super();

        effect(() => {
            this.formGroup.patchValue({
                value: this.formInputs()?.value ?? '',
                backgroundColor: this.formInputs()?.backgroundColor ?? null,
                verticalAlignment: this.formInputs()?.verticalAlignment ?? 'middle'
            });
        });

        this.formGroup.valueChanges.pipe(untilDestroyed(this)).subscribe(() => this.formValueChanged());
    }

    formValueChanged() {
        const formChangedEvent: TemplateFormChangedEvent = {
            isValid: this.formGroup.valid,
            formValues: this.formGroup.value
        };
        this.config.emit(formChangedEvent);
    }

    triggerInput(inputId: string): void {
        document.getElementById(inputId)?.click();
    }

    clearBgColor(): void {
        this.formGroup.controls.backgroundColor.patchValue(null);
        this.formGroup.controls.backgroundColor.markAsDirty({ onlySelf: true });
    }
}
